import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Quote, Star } from "lucide-react";
import { CTABanner } from "../components/CTABanner";
import { SEO } from "../components/SEO";
import { FEATURED_TESTIMONIALS, SHORT_TESTIMONIALS } from "../data/siteData";

const ROTATE_MS = 7000;

const ACCENTS = {
  teal: { chip: "bg-teal-soft text-teal-dark", dot: "bg-teal", bar: "bg-teal", glyph: "text-teal", ring: "border-teal" },
  leaf: { chip: "bg-leaf-soft text-leaf-dark", dot: "bg-leaf", bar: "bg-leaf", glyph: "text-leaf", ring: "border-leaf" },
  sunny: { chip: "bg-sunny-soft text-sunny-dark", dot: "bg-sunny", bar: "bg-sunny", glyph: "text-sunny", ring: "border-sunny" },
};

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1 -mb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => (
  <header data-testid="testimonials-hero" className="relative overflow-hidden">
    <div aria-hidden="true" className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full bg-butter-soft blur-2xl opacity-80" />
    <div aria-hidden="true" className="pointer-events-none absolute top-40 -left-32 w-72 h-72 rounded-full bg-teal-soft blur-2xl opacity-70" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 pb-10">
      <motion.span
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="inline-block bg-lime-soft text-navy font-extrabold text-xs uppercase tracking-[0.18em] px-4 py-2 rounded-full"
      >
        Testimonials
      </motion.span>
      <h1
        data-testid="testimonials-heading"
        className="mt-6 font-display text-4xl sm:text-5xl lg:text-7xl font-semibold text-navy tracking-tight leading-[1.05]"
      >
        <MaskedLine delay={0.1}>What <span className="text-turquoise">Families</span></MaskedLine>
        <MaskedLine delay={0.25}>Are Saying</MaskedLine>
      </h1>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.5, ease: "easeOut" }}
        className="mt-6 max-w-2xl text-base sm:text-lg text-navy-light leading-relaxed"
      >
        The families we serve are at the heart of Thrive. Here's what some of them have shared about their experience with our team.
      </motion.p>
    </div>
  </header>
);

const Carousel = () => {
  const [index, setIndex] = useState(() => Math.floor(Math.random() * FEATURED_TESTIMONIALS.length));
  const [direction, setDirection] = useState(1);
  const [paused, setPaused] = useState(false);
  const total = FEATURED_TESTIMONIALS.length;
  const current = FEATURED_TESTIMONIALS[index];
  const accent = ACCENTS[current.accent] || ACCENTS.teal;

  const goTo = useCallback(
    (next, dir) => {
      setDirection(dir);
      setIndex(((next % total) + total) % total);
    },
    [total]
  );

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => goTo(index + 1, 1), ROTATE_MS);
    return () => clearInterval(timer);
  }, [index, paused, goTo]);

  return (
    <section
      data-testid="testimonial-carousel"
      className="quote-stage relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <Quote
        aria-hidden="true"
        className={`pointer-events-none absolute -top-2 left-4 sm:left-10 w-32 h-32 sm:w-44 sm:h-44 ${accent.glyph} opacity-[0.12] fill-current transition-colors duration-700`}
      />

      <div className="relative bg-white rounded-[2.5rem] border border-border shadow-xl shadow-navy/5 px-6 py-10 sm:px-14 sm:py-14 overflow-hidden">
        <div className={`absolute top-0 left-0 right-0 h-1.5 bg-cream`}>
          <div
            key={`${index}-${paused}`}
            data-testid="testimonial-progress"
            className={`quote-progress h-full ${accent.bar} ${paused ? "paused" : ""}`}
          />
        </div>

        <div className="flex items-center justify-between gap-4">
          <span data-testid="testimonial-counter" className="font-display font-semibold text-sm tracking-[0.2em] text-navy-light">
            {String(index + 1).padStart(2, "0")} <span className="text-navy-light/50">/ {String(total).padStart(2, "0")}</span>
          </span>
          <span className="flex gap-1" aria-label="5 star review">
            {[...Array(5)].map((_, s) => (
              <Star key={s} className="w-4 h-4 text-butter fill-butter" />
            ))}
          </span>
        </div>

        <div className="relative min-h-[300px] sm:min-h-[260px] mt-8">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.blockquote
              key={index}
              custom={direction}
              initial={{ opacity: 0, x: direction * 60, rotate: direction * 0.5 }}
              animate={{ opacity: 1, x: 0, rotate: 0 }}
              exit={{ opacity: 0, x: direction * -60, rotate: direction * -0.5 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            >
              <p data-testid="testimonial-quote" className="font-display text-xl sm:text-2xl lg:text-[1.7rem] font-medium text-navy leading-snug">
                "{current.quote}"
              </p>
              <footer className="mt-8 flex flex-wrap items-center gap-4">
                <span className={`w-12 h-12 rounded-full ${accent.chip} flex items-center justify-center font-display font-semibold text-lg`}>
                  {current.author.charAt(0)}
                </span>
                <div>
                  <p data-testid="testimonial-author" className="font-extrabold text-base text-navy">{current.author}</p>
                  <span data-testid="testimonial-service" className={`mt-1 inline-block text-xs font-extrabold uppercase tracking-[0.14em] px-3 py-1 rounded-full ${accent.chip}`}>
                    {current.service}
                  </span>
                </div>
              </footer>
            </motion.blockquote>
          </AnimatePresence>
        </div>

        <div className="mt-8 flex items-center justify-between gap-6">
          <div className="flex items-center gap-2" role="tablist" aria-label="Choose testimonial">
            {FEATURED_TESTIMONIALS.map((t, i) => (
              <button
                key={i}
                data-testid={`testimonial-dot-${i}`}
                onClick={() => goTo(i, i > index ? 1 : -1)}
                aria-label={`Show testimonial ${i + 1}`}
                aria-current={i === index}
                className="p-1.5 flex items-center justify-center"
              >
                <span className={`block h-2.5 rounded-full transition-all duration-500 ${i === index ? `w-7 ${ACCENTS[t.accent]?.dot || "bg-teal"}` : "w-2.5 bg-navy/15 hover:bg-navy/30"}`} />
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="testimonial-prev-button"
              onClick={() => goTo(index - 1, -1)}
              aria-label="Previous testimonial"
              className="w-12 h-12 rounded-full border-2 border-navy/10 bg-white flex items-center justify-center text-navy transition-all duration-300 hover:border-navy hover:bg-navy hover:text-cream hover:-translate-x-0.5 active:scale-95"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={2.5} />
            </button>
            <button
              data-testid="testimonial-next-button"
              onClick={() => goTo(index + 1, 1)}
              aria-label="Next testimonial"
              className="w-12 h-12 rounded-full border-2 border-navy/10 bg-white flex items-center justify-center text-navy transition-all duration-300 hover:border-navy hover:bg-navy hover:text-cream hover:translate-x-0.5 active:scale-95"
            >
              <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

const ShortQuoteCard = ({ item, testId }) => {
  const accent = ACCENTS[item.accent] || ACCENTS.teal;
  return (
    <figure
      data-testid={testId}
      className="w-[320px] sm:w-[380px] shrink-0 bg-cream rounded-[1.75rem] border border-border p-7 transition-transform duration-300 hover:-translate-y-1"
    >
      <Quote className={`w-7 h-7 ${accent.glyph} fill-current opacity-70`} />
      <blockquote className="mt-4 text-sm sm:text-base text-navy leading-relaxed font-medium">
        "{item.quote}"
      </blockquote>
      <figcaption className="mt-5">
        <p className="font-extrabold text-sm text-navy">{item.author}</p>
        <p className="text-xs font-bold text-navy-light mt-0.5">{item.service}</p>
      </figcaption>
    </figure>
  );
};

const Marquee = () => {
  const items = [...SHORT_TESTIMONIALS, ...SHORT_TESTIMONIALS];
  return (
    <section data-testid="testimonials-marquee" className="marquee relative bg-white border-y border-border py-10 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6 flex items-center gap-4">
        <span className="font-display font-semibold text-xs uppercase tracking-[0.25em] text-navy-light">Little wins, big feelings</span>
        <span className="h-px flex-1 bg-border" />
      </div>
      <div className="marquee-track flex gap-6 w-max px-6">
        {items.map((item, i) => (
          <ShortQuoteCard key={i} item={item} testId={`short-testimonial-${i % SHORT_TESTIMONIALS.length}`} />
        ))}
      </div>
    </section>
  );
};

export default function Testimonials() {
  return (
    <div data-testid="testimonials-page">
      <SEO
        title="What Families Are Saying | Thrive Pediatric Therapy, Nassau"
        description="Real experiences from Bahamian families at Thrive Pediatric Therapy in Nassau - parent stories about pediatric physiotherapy, occupational therapy and feeding therapy."
      />
      <style>{`
        @keyframes quote-progress { from { transform: scaleX(0); } to { transform: scaleX(1); } }
        .quote-progress { transform-origin: left; animation: quote-progress ${ROTATE_MS}ms linear forwards; }
        .quote-progress.paused { animation-play-state: paused; }
        @keyframes testimonial-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .marquee-track { animation: testimonial-marquee 55s linear infinite; }
        .marquee:hover .marquee-track { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) {
          .quote-progress, .marquee-track { animation: none; }
        }
      `}</style>
      <Hero />
      <Carousel />
      <Marquee />
      <CTABanner title="Your child's success story starts here" subtitle="Join the Thrive family - book an initial evaluation today." />
    </div>
  );
}
